import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'

/**
 * The org's L3 tech teams, from `GET /support-tickets/teams`.
 *
 * The list used to be hardcoded here and in the backend. It is now an org setting edited under
 * Settings → Support teams; each team links to a Tapwork team whose lead and members are the
 * people an L3 ticket can be assigned to, and the server resolves that list so the UI never
 * re-derives eligibility by matching team names.
 */
export interface SupportTeamMember {
  id: string
  displayName: string
  email: string
  photoUrl: string | null
  isLead: boolean
}

export interface SupportTeam {
  /** The identifier stored on tickets. Never renamed once saved. */
  name: string
  tapworkTeamId: string | null
  slackChannelId: string | null
  slackChannelName: string | null
  active: boolean
  linkedBy: 'id' | 'name' | null
  resolvedTapworkTeamId: string | null
  tapworkTeamName: string | null
  members: SupportTeamMember[]
}

/** What the pages showed before the list was configurable — used only until the query resolves. */
export const FALLBACK_SUPPORT_TEAM_NAMES: readonly string[] = ['EMMA', 'NOVA', 'SERA', 'TOVI', 'Platform Integrations']

export function useSupportTeams(options: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: qk.supportTeams(),
    queryFn: () => apiGet<{ items: SupportTeam[] }>('support-tickets/teams'),
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  })
  const teams = query.data?.items
  const activeNames = useMemo(
    () => (teams ? teams.filter((team) => team.active).map((team) => team.name) : [...FALLBACK_SUPPORT_TEAM_NAMES]),
    [teams],
  )
  /** Every configured name, including deactivated ones — for filters over historical tickets. */
  const allNames = useMemo(() => (teams ? teams.map((team) => team.name) : [...FALLBACK_SUPPORT_TEAM_NAMES]), [teams])
  const byName = useCallback((name: string | null | undefined) => (name ? teams?.find((team) => team.name === name) ?? null : null), [teams])
  return { query, teams: teams ?? [], activeNames, allNames, byName }
}
